// HTTP Client with cookie-based auth
// - All requests include `credentials: 'include'` so HTTP-only cookies are sent
// - Automatic retry logic with exponential backoff
// - Timeout handling with AbortController
// - 401 handling triggers auth store clear

import { useAuthStore } from '../state_management_and_stores/auth.store';

export interface HttpClientConfig {
  baseUrl?: string;
  timeout?: number;
  retries?: number;
}

/**
 * Per-request overrides. Use for long-running operations that need a
 * larger timeout, or for non-idempotent mutations where a retry would
 * cause a double-execution race (e.g. bulk draw initialization where a
 * background-still-running first attempt + a retry produces a phantom
 * "skipped" count).
 */
export interface RequestOptions {
  timeout?: number;
  retries?: number;
  /**
   * External abort signal (e.g. the one React Query passes to `queryFn`).
   * When it fires — a component unmounts, or a newer fetch supersedes this
   * one — the in-flight request is aborted instead of running to completion.
   * This is what stops the duplicate/stale entries-ledger fetch (React
   * StrictMode's mount→unmount→remount) from firing a wasted second request.
   */
  signal?: AbortSignal;
}

class HttpClient {
  private baseUrl: string;
  private timeout: number;
  private retries: number;

  constructor(config: HttpClientConfig = {}) {
    this.baseUrl = config.baseUrl || import.meta.env.VITE_API_URL || '/api';
    this.timeout = config.timeout || 10000;
    this.retries = config.retries || 2;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requestOverrides: RequestOptions = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const timeoutMs = requestOverrides.timeout ?? this.timeout;
    // `?? this.retries` so callers can opt INTO retries=0 by passing it
    // explicitly (Bulk-draft mutations rely on this). `|| this.retries`
    // would have silently restored the default for the legitimate
    // zero-retry case.
    const maxRetries = requestOverrides.retries ?? this.retries;
    const externalSignal = requestOverrides.signal;

    // Already cancelled before we even start (e.g. query torn down synchronously).
    if (externalSignal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    const requestOptions: RequestInit = {
      ...options,
      credentials: 'include', // Send HTTP-only cookies on every request
      headers: {
        // Only set Content-Type for requests with a body
        ...(options.body && { 'Content-Type': 'application/json' }),
        ...options.headers
      }
    };

    let lastError: Error;

    // Retry logic
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // Per-attempt controller: aborts on our timeout OR when the caller's
      // external signal fires. Listener is cleaned up in `finally` so a
      // completed request doesn't leak a handler on the external signal.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const onExternalAbort = () => controller.abort();
      if (externalSignal) {
        externalSignal.addEventListener('abort', onExternalAbort, { once: true });
      }

      try {
        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Handle authentication errors — clear local auth state
        if (response.status === 401) {
          console.warn('[HTTP] 401 Unauthorized — clearing auth state');
          useAuthStore.getState().clearAuth();
          throw new Error('Authentication required');
        }

        if (!response.ok) {
          // Try to extract server error message + structured payload from
          // the response body. The full body is attached to the error so
          // callers can read typed fields (`code`, `unpaid_events`,
          // `organization_id`, …) for status codes that carry domain
          // metadata — 402 UNPAID_BALANCE is the load-bearing case today.
          let serverMessage = '';
          let errorBody: any = null;
          try {
            errorBody = await response.json();
            serverMessage = errorBody?.error || errorBody?.message || '';
          } catch {
            // Response body not JSON — fall back to status text
          }
          // Tag the error with the HTTP status so the retry guard below
          // can suppress retries on 4xx without relying on the message text.
          const err = new Error(
            serverMessage || `HTTP ${response.status}: ${response.statusText}`
          ) as Error & {
            status?: number;
            body?: unknown;
            code?: string;
          };
          err.status = response.status;
          if (errorBody && typeof errorBody === 'object') {
            err.body = errorBody;
            // Lift the structured `code` to the top of the error so
            // callers can branch on it without unwrapping `body`.
            if (typeof errorBody.code === 'string') {
              err.code = errorBody.code;
            }
          }
          throw err;
        }

        // Handle responses with no content (like DELETE operations)
        if (response.status === 204 || response.headers.get('content-length') === '0') {
          return undefined as T;
        }

        // Check if response has content to parse
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          return data;
        }

        // For non-JSON responses, return undefined
        return undefined as T;

      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Request failed');

        // Caller cancelled (unmount / superseded fetch) — surface the abort
        // immediately; never retry a request the caller no longer wants.
        if (externalSignal?.aborted) {
          throw lastError;
        }

        // Don't retry on auth errors or any client error (4xx).
        // The status was attached to the error above so we don't have to
        // pattern-match on the message text — backends commonly return a
        // friendly message ("Already exists", "Not authorised", ...) that
        // would otherwise slip past a substring check and trigger costly
        // retries on errors that will never recover.
        if (error instanceof Error) {
          const status = (error as Error & { status?: number }).status;
          if (status && status >= 400 && status < 500) {
            throw error;
          }
          if (error.message.includes('Authentication')) {
            throw error;
          }
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      } finally {
        clearTimeout(timeoutId);
        externalSignal?.removeEventListener('abort', onExternalAbort);
      }
    }

    throw lastError!;
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, options);
  }

  async post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }, options);
  }

  async put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }, options);
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }, options);
  }

  async delete<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined
    }, options);
  }
}

export const httpClient = new HttpClient();
